import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, FileSpreadsheet, RefreshCw } from 'lucide-react'
import { useActiveChannelId, useHasPermission } from '../../store/index.js'
import { announcementsApi, type Announcement, type AnnouncementList, type MemberSummary, type SheetSyncState } from '../../lib/infoserviceApi.js'
import { sheetSyncAgo } from '../../lib/sheetSyncText.js'
import { slideshowApi } from '../../lib/infoserviceSlideshowApi.js'
import { todayBroadcastDayKey } from '../../lib/broadcast-day.js'
import { addDays, DISPLAY_SEC_DEFAULT, formatLoop, INTRO_SEC_DEFAULT } from '../../lib/announcementGrid.js'
import { PageHeader, PageTabs } from '../common/PageChrome.js'
import { Spinner } from '../common/Spinner.js'
import { SlideshowSettings } from './SlideshowSettings.js'
import { SheetSyncSettings } from './SheetSyncSettings.js'
import { AnnouncementsTable } from './AnnouncementsTable.js'
import { AnnouncementDrawer } from './AnnouncementDrawer.js'
import { NewAnnouncementModal } from './NewAnnouncementModal.js'
import { SheetImportModal } from './SheetImportModal.js'
import { MembersPanel } from './MembersPanel.js'
import { AirtimePanel } from './AirtimePanel.js'
import '../ads/AdsView.css'
import './InfoserviceView.css'

type TabId = 'announcements' | 'members' | 'airtime' | 'settings'

const TABS: { id: TabId; label: string }[] = [
  { id: 'announcements', label: 'Annonces' },
  { id: 'members',       label: 'Adhérents' },
  { id: 'airtime',       label: 'Suivi' },
  { id: 'settings',      label: 'Réglages' },
]

/** Fenêtre du tableau : deux semaines avant le jour de référence, six après. */
const DAYS_BEFORE = 14
const WINDOW_DAYS = 56
/** Relecture de l'état de la feuille : une synchronisation de fond rafraîchit le tableau. */
const SHEET_POLL_MS = 60_000

export type ArchiveView = 'active' | 'archived'

export const InfoserviceView = memo(function InfoserviceView({ now }: { now?: Date } = {}) {
  const channelId = useActiveChannelId()
  const canManage = useHasPermission('ADS_MANAGE')
  const today = useMemo(() => todayBroadcastDayKey(now ?? new Date()), [now])
  const [tab, setTab] = useState<TabId>('announcements')
  const [anchor, setAnchor] = useState(() => addDays(today, -DAYS_BEFORE))
  const [view, setView] = useState<ArchiveView>('active')
  const [search, setSearch] = useState('')
  const [data, setData] = useState<AnnouncementList | null>(null)
  const [members, setMembers] = useState<MemberSummary[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [slideshowMode, setSlideshowMode] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // La fiche ouverte porte des saisies non enregistrées (tenu par la fiche).
  const drawerDirty = useRef(false)
  const [detail, setDetail] = useState<Announcement | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [sheet, setSheet] = useState<SheetSyncState | null>(null)
  const [syncing, setSyncing] = useState(false)

  const to = addDays(anchor, WINDOW_DAYS - 1)

  const load = useCallback(async () => {
    if (!channelId) return
    setLoading(true)
    setError('')
    try {
      setData(await announcementsApi.list({ channelId, from: anchor, to, archived: view, day: today }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [anchor, channelId, to, today, view])

  useEffect(() => { void load() }, [load])

  // État de la feuille Google ; une lecture de fond plus récente recharge le tableau.
  useEffect(() => {
    if (!channelId) return undefined
    let cancelled = false
    let lastSuccess: string | null | undefined
    const poll = async () => {
      try {
        const state = await announcementsApi.sheetSync.state(channelId)
        if (cancelled) return
        setSheet(state)
        if (lastSuccess !== undefined && state.lastSuccessAt !== lastSuccess) void load()
        lastSuccess = state.lastSuccessAt
      } catch {
        // Lecture de confort : le tableau reste utilisable sans elle.
      }
    }
    void poll()
    const timer = window.setInterval(() => { void poll() }, SHEET_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [channelId, load])

  const syncNow = useCallback(async () => {
    if (!channelId) return
    setSyncing(true)
    setError('')
    try {
      const { state, result } = await announcementsApi.sheetSync.run(channelId)
      setSheet(state)
      if (result.status === 'REFUSED') setError(`Feuille non appliquée : ${result.message} Voir Réglages › Feuille Google.`)
      if (result.applied) void load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Synchronisation impossible')
    } finally {
      setSyncing(false)
    }
  }, [channelId, load])

  const sheetConnected = Boolean(sheet?.connected)

  // Adhérents : la liste sert à l'onglet, à la fiche et à la création.
  const loadMembers = useCallback(async () => {
    if (!channelId) return
    setMembersLoading(true)
    try {
      setMembers(await announcementsApi.members.list(channelId))
    } catch {
      setMembers([])
    } finally {
      setMembersLoading(false)
    }
  }, [channelId])

  useEffect(() => { void loadMembers() }, [loadMembers])

  // Le suivi n'existe qu'en diaporama : l'onglet le dit en mode fichier vidéo.
  useEffect(() => {
    if (!channelId || tab !== 'airtime') return
    slideshowApi.get(channelId)
      .then(settings => setSlideshowMode(settings.config.mode === 'SLIDESHOW'))
      .catch(() => setSlideshowMode(null))
  }, [channelId, tab])

  // La fiche relit l'annonce complète (tous ses jours, ses commentaires) :
  // la liste ne porte que la fenêtre affichée.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    let cancelled = false
    announcementsApi.get(selectedId)
      .then(announcement => { if (!cancelled) setDetail(announcement) })
      .catch(() => { if (!cancelled) setSelectedId(null) })
    return () => { cancelled = true }
  }, [selectedId])

  /** Quitter la fiche : confirmation si des saisies ne sont pas enregistrées. */
  const confirmLeaveDrawer = useCallback(() => {
    if (!drawerDirty.current) return true
    if (!window.confirm('Les modifications de cette annonce ne sont pas enregistrées. Les abandonner ?')) return false
    drawerDirty.current = false
    return true
  }, [])

  const openAnnouncement = useCallback((id: string | null) => {
    if (id === selectedId || !confirmLeaveDrawer()) return
    setSelectedId(id)
  }, [confirmLeaveDrawer, selectedId])

  const markDrawerDirty = useCallback((dirty: boolean) => { drawerDirty.current = dirty }, [])

  /** Remplace l'annonce modifiée dans la liste et rafraîchit le plan du jour. */
  const applyUpdate = useCallback((updated: Announcement) => {
    if (updated.id === selectedId) setDetail(updated)
    void load()
    // L'adhérent a pu changer, ou naître d'un nouvel organisme.
    void loadMembers()
  }, [load, loadMembers, selectedId])

  const toggleDay = useCallback(async (announcement: Announcement, day: string) => {
    const checked = announcement.days.includes(day)
    // Écho immédiat : cocher une case doit répondre sous le doigt, la
    // réponse du serveur remplace ensuite la ligne.
    setData(current => current && {
      ...current,
      announcements: current.announcements.map(row => row.id !== announcement.id ? row : {
        ...row,
        days: checked ? row.days.filter(entry => entry !== day) : [...row.days, day].sort(),
      }),
    })
    try {
      applyUpdate(await announcementsApi.setDays(announcement.id, checked ? { remove: [day] } : { add: [day] }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Modification impossible')
      void load()
    }
  }, [applyUpdate, load])

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('fr')
    const rows = data?.announcements ?? []
    if (!needle) return rows
    return rows.filter(row => [row.organizationLabel, row.eventLabel, row.memberName ?? '']
      .some(value => value.toLocaleLowerCase('fr').includes(needle)))
  }, [data, search])

  const plan = data?.dayPlan ?? null
  const onAirCount = plan?.onAirIds.length ?? 0
  const scheduledCount = plan?.scheduledIds.length ?? 0
  const firstLoopSec = onAirCount > 0 ? INTRO_SEC_DEFAULT + (plan?.loopSec ?? 0) : 0

  const handleCreated = useCallback((created: Announcement) => {
    setCreating(false)
    setView('active')
    openAnnouncement(created.id)
    void load()
    void loadMembers()
  }, [load, loadMembers, openAnnouncement])

  const handleImported = useCallback(() => {
    setImporting(false)
    void load()
  }, [load])

  const handleDeleted = useCallback(() => {
    drawerDirty.current = false
    setSelectedId(null)
    void load()
  }, [load])

  return (
    <div className="tp-page-shell infoservice-view">
      <PageHeader
        title="Infoservice"
        actions={tab === 'announcements' && sheetConnected && sheet ? (
          <>
            <span className={`announcements-sheet${sheet.lastStatus && sheet.lastStatus !== 'OK' ? ' is-alert' : ''}`}>
              {sheet.lastStatus && sheet.lastStatus !== 'OK'
                ? <AlertTriangle size={14} aria-hidden="true" />
                : <FileSpreadsheet size={14} aria-hidden="true" />}
              {sheet.lastStatus === 'REFUSED' ? 'Feuille Google non appliquée'
                : sheet.lastStatus === 'ERROR' ? 'Feuille Google illisible'
                  : `Feuille Google${sheet.lastSuccessAt ? ` · lue ${sheetSyncAgo(sheet.lastSuccessAt)}` : ''}`}
            </span>
            {sheet.editUrl && (
              <a className="tp-button" href={sheet.editUrl} target="_blank" rel="noreferrer">Ouvrir la feuille</a>
            )}
            <button
              type="button"
              className="tp-button tp-button--primary"
              onClick={() => void syncNow()}
              disabled={!canManage || syncing}
              title={!canManage ? 'Permission de gestion publicitaire requise' : 'Relire la feuille Google tout de suite'}
            >
              {syncing ? <Spinner /> : <RefreshCw size={14} aria-hidden="true" />} Synchroniser
            </button>
          </>
        ) : tab === 'announcements' ? (
          <>
            <button
              type="button"
              className="tp-button"
              onClick={() => setImporting(true)}
              disabled={!channelId || !canManage}
              title={!canManage ? 'Permission de gestion publicitaire requise' : 'Reprendre la feuille Google « Infographies » (.xlsx)'}
            >
              Importer la feuille…
            </button>
            <button
              type="button"
              className="tp-button tp-button--primary"
              onClick={() => setCreating(true)}
              disabled={!channelId || !canManage}
              title={!canManage ? 'Permission de gestion publicitaire requise' : undefined}
            >
              + Nouvelle annonce
            </button>
          </>
        ) : undefined}
      />

      <PageTabs
        items={TABS}
        activeId={tab}
        onChange={id => { if (id === tab || confirmLeaveDrawer()) setTab(id as TabId) }}
        ariaLabel="Sections de l’infoservice"
        controlsId="infoservice-tab-panel"
        idPrefix="infoservice-tab"
      />

      {tab === 'announcements' && (
        <section id="infoservice-tab-panel" role="tabpanel" aria-labelledby="infoservice-tab-announcements" className="announcements-workspace">
          <div className="announcements-summary">
            <div className="announcements-summary__title">
              <h2 className="announcements-summary__heading">Annonces</h2>
              <p className="announcements-summary__note">
                {sheetConnected
                  ? 'Les annonces viennent de la feuille Google de la secrétaire : reliez ici les images, la priorité et la durée.'
                  : 'L’antenne diffuse encore le fichier MP4 du jour (onglet Réglages) : ces annonces passeront en diaporama quand le mode sera activé.'}
              </p>
            </div>
            <div className="announcements-summary__metric">
              <span className="announcements-summary__label">À l’antenne aujourd’hui</span>
              <span className="announcements-summary__value">{onAirCount}</span>
              <span className="announcements-summary__label">sur {scheduledCount} annonce{scheduledCount > 1 ? 's' : ''} cochée{scheduledCount > 1 ? 's' : ''}</span>
            </div>
            <div className="announcements-summary__metric">
              <span className="announcements-summary__label">Durée d’un tour</span>
              <span className="announcements-summary__value">{onAirCount ? formatLoop(firstLoopSec) : '—'}</span>
              <span className="announcements-summary__label">
                {onAirCount
                  ? <>intro {INTRO_SEC_DEFAULT} s + {onAirCount} × {DISPLAY_SEC_DEFAULT} s, puis {formatLoop(plan?.loopSec ?? 0)}</>
                  : 'aucune annonce diffusable'}
              </span>
            </div>
            <div className="announcements-summary__tools">
              <input
                className="tp-input"
                type="search"
                placeholder="Organisme, événement, adhérent…"
                aria-label="Rechercher une annonce"
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
              <div className="tp-segmented" aria-label="Annonces affichées">
                <button type="button" className={`tp-segment${view === 'active' ? ' is-active' : ''}`} aria-pressed={view === 'active'} onClick={() => setView('active')}>En cours</button>
                <button type="button" className={`tp-segment${view === 'archived' ? ' is-active' : ''}`} aria-pressed={view === 'archived'} onClick={() => setView('archived')}>Archivées</button>
              </div>
            </div>
          </div>

          {plan && plan.missingImageIds.length > 0 && view === 'active' && (
            <AttentionBanner missingImage={plan.missingImageIds.length} />
          )}
          {error && <div className="tp-feedback tp-feedback--error announcements-feedback" role="alert">{error}</div>}

          {loading && !data ? (
            <div className="tp-empty-state announcements-state" aria-live="polite"><Spinner /> Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="tp-empty-state announcements-state">
              {search ? 'Aucune annonce ne correspond à la recherche.'
                : view === 'archived' ? 'Aucune annonce archivée.'
                  : sheetConnected ? 'Aucune annonce en cours dans la feuille Google.'
                    : 'Aucune annonce. Reliez la feuille Google (Réglages) ou créez une annonce.'}
            </div>
          ) : (
            <AnnouncementsTable
              announcements={filtered}
              from={anchor}
              days={WINDOW_DAYS}
              today={today}
              selectedId={selectedId}
              canManage={canManage}
              onSelect={openAnnouncement}
              onToggleDay={toggleDay}
              onShift={delta => setAnchor(current => addDays(current, delta))}
              onToday={() => setAnchor(addDays(today, -DAYS_BEFORE))}
            />
          )}

          {detail && (
            <AnnouncementDrawer
              key={detail.id}
              announcement={detail}
              members={members}
              today={today}
              canManage={canManage}
              channelId={channelId}
              sheetEditUrl={sheet?.editUrl ?? null}
              onClose={() => openAnnouncement(null)}
              onUpdated={applyUpdate}
              onDuplicated={created => { setSelectedId(created.id); void load() }}
              onDeleted={handleDeleted}
              onDirtyChange={markDrawerDirty}
            />
          )}
        </section>
      )}

      {tab === 'members' && (
        <MembersPanel
          channelId={channelId}
          members={members}
          loading={membersLoading}
          canManage={canManage}
          today={today}
          onChanged={() => { void loadMembers(); void load() }}
          onOpenAnnouncement={id => { setTab('announcements'); openAnnouncement(id) }}
        />
      )}

      {tab === 'airtime' && <AirtimePanel channelId={channelId} today={today} slideshow={slideshowMode} />}

      {tab === 'settings' && (
        <section
          id="infoservice-tab-panel"
          role="tabpanel"
          aria-labelledby="infoservice-tab-settings"
          className="infoservice-settings-panel"
          title={!canManage ? 'Consultation seule — permission de gestion publicitaire requise' : undefined}
        >
          <div className="sheet-sync-settings">
            <SheetSyncSettings
              channelId={channelId}
              canManage={canManage}
              onSynced={() => {
                void load()
                if (channelId) announcementsApi.sheetSync.state(channelId).then(setSheet).catch(() => {})
              }}
            />
          </div>
          <SlideshowSettings channelId={channelId} canManage={canManage} onShowAnnouncements={() => setTab('announcements')} />
        </section>
      )}

      {creating && channelId && (
        <NewAnnouncementModal
          channelId={channelId}
          members={members}
          today={today}
          onCreated={handleCreated}
          onClose={() => setCreating(false)}
        />
      )}
      {importing && channelId && (
        <SheetImportModal channelId={channelId} onImported={handleImported} onClose={() => setImporting(false)} />
      )}
    </div>
  )
})

const AttentionBanner = memo(function AttentionBanner({ missingImage }: { missingImage: number }) {
  return (
    <div className="announcements-attention is-blocking" role="status">
      <AlertTriangle size={14} aria-hidden="true" />
      <span>{missingImage} annonce{missingImage > 1 ? 's' : ''} cochée{missingImage > 1 ? 's' : ''} aujourd’hui sans image — elle{missingImage > 1 ? 's' : ''} ne passe{missingImage > 1 ? 'nt' : ''} pas.</span>
    </div>
  )
})
